import { useEffect, useState } from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewStyle, type ImageStyle } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from 'tamagui';
import { colors } from '../../theme/colors';

/** Palette cycled by hashing the label string. */
const GRADIENT_PALETTES: [string, string][] = [
  ['#2D6A4F', '#1B4332'],
  ['#1D3557', '#457B9D'],
  ['#6D2B6E', '#A855C8'],
  ['#7F4F24', '#D08B2D'],
  ['#2B4162', '#12664F'],
  ['#C05621', '#7B341E'],
  ['#065F46', '#34D399'],
  ['#4C1D95', '#7C3AED'],
];

function paletteFor(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return GRADIENT_PALETTES[Math.abs(h) % GRADIENT_PALETTES.length]!;
}

interface FallbackImageProps {
  /** Image source URI — may be empty/null to go straight to fallback. */
  uri: string | null | undefined;
  /** Text used to derive the initial letter for the fallback. */
  label: string;
  /** Optional stable seed for the gradient palette. Use this when the
   *  label can change (e.g. participant rename) but the underlying
   *  identity is stable — passing the entity id keeps the gradient
   *  pinned across renames. Defaults to `label`. */
  seed?: string | undefined;
  style?: StyleProp<ImageStyle> | undefined;
  containerStyle?: StyleProp<ViewStyle> | undefined;
  contentFit?: 'cover' | 'contain' | 'fill' | undefined;
  transition?: number | undefined;
  /** Font size for the initial letter (default 32). */
  letterSize?: number | undefined;
  borderRadius?: number | undefined;
}

/**
 * Drop-in replacement for expo-image Image that falls back to a branded
 * gradient + initial letter when the photo fails or is absent.
 */
export function FallbackImage({
  uri,
  label,
  seed,
  style,
  containerStyle,
  contentFit = 'cover',
  transition = 200,
  letterSize = 32,
  borderRadius,
}: FallbackImageProps) {
  const [errored, setErrored] = useState(false);
  // Reset error state when the source URI changes — without this, switching
  // a trip's locked cover photo (or any source change) leaves the gradient
  // fallback stuck even though the new URI is valid.
  useEffect(() => {
    setErrored(false);
  }, [uri]);
  const showFallback = !uri || errored;
  const initial = (label.trim()[0] ?? '?').toUpperCase();
  const [start, end] = paletteFor(seed ?? label);

  const flatStyle = StyleSheet.flatten(style) as Record<string, unknown> | undefined;
  const resolvedRadius = borderRadius ?? (flatStyle?.borderRadius as number | undefined) ?? 0;

  if (showFallback) {
    return (
      <LinearGradient
        colors={[start, end]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[style as StyleProp<ViewStyle>, containerStyle, { alignItems: 'center', justifyContent: 'center', borderRadius: resolvedRadius }] as never}
      >
        <Text
          fontSize={letterSize}
          fontWeight="800"
          color={colors.textOnDarkMuted}
          style={{ letterSpacing: 1 }}
        >
          {initial}
        </Text>
      </LinearGradient>
    );
  }

  // On web, expo-image silently drops the `alt` prop, so every hero/avatar
  // routed through here failed the image-alt A11y/SEO audits. Render a real
  // <img> wrapped in a View (the View converts the RN style to CSS; the img
  // just fills it and carries the alt). The wrapping div is transparent to
  // layout. Native keeps expo-image (caching + cross-fade).
  if (Platform.OS === 'web') {
    return (
      <View
        style={[style, { borderRadius: resolvedRadius, overflow: 'hidden' }] as StyleProp<ViewStyle>}
      >
        <img
          src={uri}
          // A blank label (decorative use) yields alt="" — still satisfies
          // the audit while staying silent to assistive tech.
          alt={label}
          onError={() => setErrored(true)}
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: contentFit, display: 'block' }}
        />
      </View>
    );
  }

  return (
    <Image
      source={{ uri }}
      style={[style, { borderRadius: resolvedRadius }] as StyleProp<ImageStyle>}
      contentFit={contentFit}
      transition={transition}
      cachePolicy="memory-disk"
      // Surface the entity label as the image's accessible name.
      alt={label}
      accessibilityLabel={label}
      onError={() => setErrored(true)}
    />
  );
}
